@if (brief.brief(); as generated) {
  <div class="min-h-0 flex-1 overflow-y-auto px-5 py-7 sm:px-8 sm:pt-12">
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-3">
      <p class="m-0 w-fit rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-bold text-emerald-800">Your Quick Brief is ready</p>
      <h1 class="use-grotesque m-0 text-4xl font-bold leading-none tracking-tight text-balance sm:text-5xl">{{ generated.title }}</h1>
      <p class="m-0 text-xl font-semibold leading-snug text-muted-foreground">Review it, then publish to start matching creators. You can edit every detail later.</p>
    </div>

    <article class="shrink-0 overflow-hidden rounded-xl border border-border bg-card shadow-md">
      <div class="flex items-center gap-3 bg-brand-studio p-5 text-white">
        <span class="flex size-12 shrink-0 items-center justify-center rounded-lg bg-white text-xl font-black text-brand-studio" aria-hidden="true">{{ brief.brandName().charAt(0) }}</span>
        <div>
          <p class="m-0 text-xl font-bold">{{ brief.summaryPromoting() || brief.brandName() }}</p>
          <p class="m-0 text-base font-semibold text-white/80">Quick Brief · {{ brief.focus() === 'product' ? 'Product' : 'Brand' }} · {{ brief.format() === 'ugc' ? 'UGC only' : brief.summaryChannels() }}</p>
        </div>
      </div>
      <div class="grid grid-cols-2 sm:grid-cols-4">
        <div class="border-b border-r border-border p-4">
          <p class="m-0 text-xs font-bold uppercase tracking-wide text-muted-foreground">Objective</p>
          <p class="m-0 mt-1 text-lg font-bold">{{ brief.objective() }}</p>
        </div>
        <div class="border-b border-border p-4 sm:border-r">
          <p class="m-0 text-xs font-bold uppercase tracking-wide text-muted-foreground">Content</p>
          <p class="m-0 mt-1 text-lg font-bold">{{ brief.summaryContent() }}</p>
        </div>
        <div class="border-b border-r border-border p-4 sm:border-b-0">
          <p class="m-0 text-xs font-bold uppercase tracking-wide text-muted-foreground">{{ brief.format() === 'ugc' ? 'Usage' : 'Channels' }}</p>
          <p class="m-0 mt-1 text-lg font-bold">{{ brief.format() === 'ugc' ? 'Your ads and socials' : brief.summaryChannels() }}</p>
        </div>
        <div class="border-b border-border p-4 sm:border-b-0">
          <p class="m-0 text-xs font-bold uppercase tracking-wide text-muted-foreground">Timeline</p>
          <p class="m-0 mt-1 text-lg font-bold">{{ brief.timeline }}</p>
        </div>
      </div>
      <div class="flex flex-col gap-7 p-5">
        <section>
          <h2 class="m-0 text-sm font-bold uppercase tracking-wide text-primary">The brief</h2>
          <p class="m-0 mt-2 text-lg font-medium leading-snug">{{ generated.summary }}</p>
        </section>
        <section>
          <h2 class="m-0 text-sm font-bold uppercase tracking-wide text-primary">Key messages</h2>
          <ul class="m-0 mt-2 list-disc space-y-1 pl-5">
            @for (message of generated.keyMessages; track message) {
              <li class="text-lg font-semibold">{{ message }}</li>
            }
          </ul>
        </section>
        <section>
          <h2 class="m-0 text-sm font-bold uppercase tracking-wide text-primary">Creative concepts</h2>
          <div class="mt-2 flex snap-x snap-mandatory gap-3 overflow-x-auto sm:grid sm:grid-cols-3 sm:overflow-visible">
            @for (concept of generated.concepts; track concept.name; let index = $index) {
              <article class="w-4/5 shrink-0 snap-start rounded-lg bg-primary/5 p-4 sm:w-auto">
                <p class="m-0 text-sm font-extrabold text-primary">0{{ index + 1 }}</p>
                <h3 class="m-0 mt-1 text-lg font-bold">{{ concept.name }}</h3>
                @if (concept.hook) {
                  <p class="m-0 mt-1 text-base font-bold text-primary">"{{ concept.hook }}"</p>
                }
                <p class="m-0 mt-1 text-base font-medium text-muted-foreground">{{ concept.description }}</p>
              </article>
            }
          </div>
          <p class="m-0 mt-2 text-sm font-semibold text-muted-foreground sm:hidden">Swipe to see each concept.</p>
        </section>
        <div class="grid gap-6 sm:grid-cols-2">
          <section>
            <h2 class="m-0 text-sm font-bold uppercase tracking-wide text-primary">Deliverables</h2>
            <ul class="m-0 mt-2 list-none space-y-1 p-0">
              @for (line of brief.deliverables(); track line) {
                <li class="text-base font-semibold">{{ line }}</li>
              }
            </ul>
          </section>
          <section>
            <h2 class="m-0 text-sm font-bold uppercase tracking-wide text-primary">Creators we'll match</h2>
            <p class="m-0 mt-2 text-base font-medium">{{ generated.creatorProfile }}</p>
            @if (generated.niches.length > 0) {
              <div class="mt-2 flex flex-wrap gap-2">
                @for (niche of generated.niches; track niche) {
                  <p class="m-0 rounded-full bg-primary/10 px-2.5 py-1 text-sm font-bold text-primary">{{ niche }}</p>
                }
              </div>
            }
          </section>
        </div>
        <div class="grid gap-3 sm:grid-cols-2">
          <section class="rounded-lg border border-border p-4">
            <h2 class="m-0 text-base font-extrabold text-emerald-800">Do</h2>
            <ul class="m-0 mt-2 list-none space-y-1 p-0">
              @for (item of generated.dos; track item) {
                <li class="text-base font-medium">{{ item }}</li>
              }
            </ul>
          </section>
          <section class="rounded-lg border border-border p-4">
            <h2 class="m-0 text-base font-extrabold text-red-700">Don't</h2>
            <ul class="m-0 mt-2 list-none space-y-1 p-0">
              @for (item of generated.donts; track item) {
                <li class="text-base font-medium">{{ item }}</li>
              }
            </ul>
          </section>
        </div>
      </div>
    </article>

    <p class="m-0 text-sm font-semibold text-muted-foreground">Generated by SHOUT AI from {{ brief.domain() || 'your site' }}. Every brief passes brand-safety moderation before creators see it.</p>

    @if (brief.error()) {
      <p class="m-0 text-base font-semibold text-red-600" role="alert">{{ brief.error() }}</p>
    }
  </div>
  </div>
  <div class="flex shrink-0 flex-col gap-2 border-t border-border bg-background px-5 py-3 sm:flex-row sm:items-center sm:px-8">
    <button type="button" class="btn-shout btn-shout-sm btn-shout-tertiary order-2 sm:order-1 sm:mr-auto" [disabled]="brief.saving()" (click)="brief.saveDraft()">Continue without publishing</button>
    <div class="order-1 flex gap-2 sm:order-2">
      <button type="button" class="btn-shout btn-shout-md btn-shout-secondary shrink-0" [disabled]="brief.saving()" (click)="brief.regenerate()">Regenerate</button>
      <button type="button" class="btn-shout btn-shout-md btn-shout-primary flex-1 sm:flex-none" [disabled]="brief.saving()" [attr.aria-busy]="brief.publishing()" (click)="brief.accept()">{{ brief.publishing() ? 'Publishing…' : 'Accept & publish' }}</button>
    </div>
  </div>
}
